"use client";

import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import { AgentPanel } from "@/components/agent/agent-panel";
import {
  getAssistantIntegration,
  isSiteReflexPublicPath,
} from "@/lib/sitereflex/integration";

const SITEREFLEX_DOCUMENT_MARKER = "sitereflexRuntimeLoaded";
const SITEREFLEX_LOADER_VALUE = "weibotop-production";
const SITEREFLEX_STATE_EVENT = "sitewake:state";
const SITEREFLEX_READY_TIMEOUT_MS = 20_000;
const SITEREFLEX_READY_MARKER_VALUE = "ready";

type SiteReflexRuntimeState = "booting" | "ready" | "error";

interface SiteReflexRuntimeStateDetail {
  state?: SiteReflexRuntimeState;
  siteAgentId?: string;
  releaseVersion?: number;
  message?: string;
}

type SiteReflexRuntimeWindow = Window & {
  SiteWake?: unknown;
  SiteWakePort?: unknown;
  SiteWakeAdaptive?: unknown;
};

function runtimeWindow(): SiteReflexRuntimeWindow {
  return window as SiteReflexRuntimeWindow;
}

function hasSiteReflexApi(): boolean {
  const runtime = runtimeWindow();
  return Boolean(runtime.SiteWake);
}

function removeSiteReflexArtifacts(): void {
  document
    .querySelectorAll(`[data-sitereflex-loader="${SITEREFLEX_LOADER_VALUE}"]`)
    .forEach((element) => element.remove());
  document
    .querySelectorAll("[data-sitewake-agent]")
    .forEach((element) => element.remove());

  const runtime = runtimeWindow();
  try {
    delete runtime.SiteWake;
    delete runtime.SiteWakePort;
    delete runtime.SiteWakeAdaptive;
  } catch {
    runtime.SiteWake = undefined;
    runtime.SiteWakePort = undefined;
    runtime.SiteWakeAdaptive = undefined;
  }
}

function SiteReflexLoader({
  enabled,
  endpoint,
  siteAgentId,
  allowedCapabilityIds,
}: {
  enabled: boolean;
  endpoint: string;
  siteAgentId: string;
  allowedCapabilityIds: string;
}) {
  const [useLegacyFallback, setUseLegacyFallback] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setUseLegacyFallback(false);
      if (
        document.documentElement.dataset[SITEREFLEX_DOCUMENT_MARKER]
      ) {
        removeSiteReflexArtifacts();
        // The SDK owns document-level listeners and timers. A hard reload is the
        // only complete teardown when an App Router transition enters a route
        // where page observation is forbidden. The marker disappears in the new
        // document, so this cannot form a reload loop.
        window.location.reload();
      }
      return;
    }

    setUseLegacyFallback(false);
    if (
      hasSiteReflexApi()
      && document.documentElement.dataset[SITEREFLEX_DOCUMENT_MARKER]
        === SITEREFLEX_READY_MARKER_VALUE
    ) {
      return () => removeSiteReflexArtifacts();
    }
    // A global is installed before the SDK has loaded its Release. Never treat
    // that global alone as readiness, including after Fast Refresh/remounts.
    delete document.documentElement.dataset[SITEREFLEX_DOCUMENT_MARKER];
    removeSiteReflexArtifacts();

    let disposed = false;
    let phase: "pending" | "ready" | "fallback" = "pending";
    let readinessTimeout: number | undefined;
    const script = document.createElement("script");
    script.async = true;
    script.src = `${endpoint}/sitewake.js`;
    script.dataset.sitereflexLoader = SITEREFLEX_LOADER_VALUE;
    script.dataset.siteAgent = siteAgentId;
    script.dataset.endpoint = endpoint;
    script.dataset.visibility = "production";
    script.dataset.allowFrame = "false";
    script.dataset.runtimeMode = "full";
    script.dataset.domReplay = "false";
    if (allowedCapabilityIds) script.dataset.allowedCapabilityIds = allowedCapabilityIds;

    const clearReadinessTimeout = () => {
      if (readinessTimeout !== undefined) {
        window.clearTimeout(readinessTimeout);
        readinessTimeout = undefined;
      }
    };

    const failOpen = () => {
      if (disposed || phase === "ready") return;
      phase = "fallback";
      clearReadinessTimeout();
      delete document.documentElement.dataset[SITEREFLEX_DOCUMENT_MARKER];
      removeSiteReflexArtifacts();
      setUseLegacyFallback(true);
    };

    const onRuntimeState = (event: Event) => {
      const detail = (event as CustomEvent<SiteReflexRuntimeStateDetail>).detail;
      if (!detail || detail.siteAgentId !== siteAgentId) return;
      if (phase === "fallback") {
        // A timed-out script may still finish after its element was removed.
        // Keep a late runtime from appearing beside the legacy fallback.
        removeSiteReflexArtifacts();
        return;
      }
      if (detail.state === "error") {
        failOpen();
        return;
      }
      if (detail.state === "ready" && hasSiteReflexApi()) {
        phase = "ready";
        clearReadinessTimeout();
        document.documentElement.dataset[SITEREFLEX_DOCUMENT_MARKER]
          = SITEREFLEX_READY_MARKER_VALUE;
      }
    };

    window.addEventListener(SITEREFLEX_STATE_EVENT, onRuntimeState);
    readinessTimeout = window.setTimeout(failOpen, SITEREFLEX_READY_TIMEOUT_MS);
    script.onerror = failOpen;
    script.onload = () => {
      if (disposed) return;
      if (phase === "fallback" || !hasSiteReflexApi()) {
        removeSiteReflexArtifacts();
        failOpen();
      }
      // Asset load only means the JavaScript evaluated. Readiness is signalled
      // separately after the SDK has successfully loaded and applied a Release.
    };
    document.head.appendChild(script);

    return () => {
      disposed = true;
      clearReadinessTimeout();
      window.removeEventListener(SITEREFLEX_STATE_EVENT, onRuntimeState);
      script.onerror = null;
      script.onload = null;
      removeSiteReflexArtifacts();
    };
  }, [allowedCapabilityIds, enabled, endpoint, siteAgentId]);

  if (!enabled) return null;
  if (useLegacyFallback) return <AgentPanel />;
  return null;
}

export function AssistantSlot() {
  const pathname = usePathname();
  const { mode, endpoint, siteAgentId, readOnlyCapabilityIds } = getAssistantIntegration();

  if (mode === "legacy") {
    return pathname.startsWith("/admin") ? null : <AgentPanel />;
  }
  if (mode === "off") return null;

  return (
    <SiteReflexLoader
      enabled={isSiteReflexPublicPath(pathname)}
      endpoint={endpoint}
      siteAgentId={siteAgentId}
      allowedCapabilityIds={readOnlyCapabilityIds.join(",")}
    />
  );
}
